import { useEffect, useRef, useState } from 'react';
import useModalActions from '@hooks/common/useModalAction';
import AVCReportModalContent from './AVCReportModalContent';
import { useConfig } from 'config/useConfig';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import { REPORT_MODAL_NAMES } from 'config/labels';
import { useReportModal } from '@hooks/useReportModal';
import {
  ContextKey,
  CPRContext,
  CrossSectionContext,
  SurfaceContext,
  useBufferPool,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import {
  captureCPROffscreen,
  captureSurface3D,
  captureView,
  cropToSquare,
  trimMarginsToContent,
} from 'utils/captureUtils';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import useAuth from '@hooks/Auth/useAuth';
import { useBranchInfo } from '@contexts/BranchContext';
import {
  captureServerAvcCprImages,
  isServerReportCprEnabled,
} from '../serverCprReportCapture';
import { captureServerCrossSectionCanvas } from '@features/analysis/rendering/Panels/Renderers/ServerCrossSectionCanvas';

const AVCReportModal = ({ onClose }: { onClose: () => void }) => {
  const names = REPORT_MODAL_NAMES.avc;
  const { config, setData } = useConfig();
  const { waitingAction, closeLoadingModal } = useModalActions();
  const [imageSrc, setImageSrc] = useState<{
    crossSection?: string;
    angle0?: string;
    angle120?: string;
    angle240?: string;
    threeD?: string;
  }>();
  const { analysisType } = usePanelContainer();
  const { currentVOI } = useAnalysisPage();
  const { selectedSeriesId } = useBrowserPage();
  const { accessToken } = useAuth();
  const { mainBranchIds } = useBranchInfo();

  const { bufferPool, contextPool } = useBufferPool();
  const poolRef = useRef(contextPool);
  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  async function capture3d(): Promise<string | undefined> {
    const d3context = {
      current: poolRef.current[`Surface3D` as ContextKey] as SurfaceContext,
    } as React.MutableRefObject<SurfaceContext | null>;
    if (!d3context) return;
    if (!d3context.current) return;

    let url = await captureSurface3D(d3context.current, analysisType);
    url = await cropToSquare(url!)
    return url;
  }

  async function captureCPR(): Promise<string[] | undefined> {
    const branchId = config.aorta_id ?? config.tavi_aorta_id ?? 0;
    if (
      isServerReportCprEnabled(analysisType) &&
      selectedSeriesId &&
      accessToken &&
      bufferPool[selectedSeriesId]
    ) {
      const serverImages = await captureServerAvcCprImages({
        accessToken,
        seriesId: selectedSeriesId,
        pool: bufferPool[selectedSeriesId],
        analysisType,
        currentVOI,
        mainBranchIds,
        branchId,
      }).catch((err) => {
        console.warn('[report][front_data] AVC CPR capture failed', err);
        return undefined;
      });
      if (serverImages?.length === 3) return serverImages;
    }

    const d3context = {
      current: poolRef.current[
        `CPR.0.straightened` as ContextKey
      ] as CPRContext,
    } as React.MutableRefObject<CPRContext | null>;
    if (!d3context) return;
    if (!d3context.current) return;
    const { labelData, centerlineData } = d3context.current;
    if (!labelData || !centerlineData) return;

    const [url] = await captureCPROffscreen(
      d3context,
      d3context.current.imageData!,
      labelData!,
      centerlineData!,
      1024,
      1024,
      120,
      3,
      analysisType,
      currentVOI,
    );

    return url;
  }

  async function captureCrossSection(): Promise<string | undefined> {
    if (isServerReportCprEnabled(analysisType)) {
      const serverCanvasCapture = await captureServerCrossSectionCanvas(
        'CrossSection.0.0',
      ).catch((err) => {
        console.warn('[report][front_data] AVC CrossSection capture failed', err);
        return undefined;
      });
      if (serverCanvasCapture) {
        const [url] = await trimMarginsToContent(serverCanvasCapture);
        return url;
      }
    }

    const d3context = {
      current: poolRef.current[
        `CrossSection.0.0` as ContextKey
      ] as CrossSectionContext,
    } as React.MutableRefObject<CrossSectionContext | null>;
    if (!d3context) return;
    if (!d3context.current) return;

    let fir = await captureView(
      {
        type: 'vtk',
        renderWindow: d3context.current.renderWindow,
      },
      { scale: 1 },
    );
    const [url] = await trimMarginsToContent(fir);
    fir = url;
    return fir;
  }

  const [imgUrl, setImgUrl] = useState<string>('');

  async function capture(){
    waitingAction({ message: 'Waiting' });
    try {
      const url = await capture3d().catch((err) => {
        console.warn('[report] AVC 3D capture failed', err);
        return undefined;
      });
      const url2 = await captureCPR().catch((err) => {
        console.warn('[report] AVC CPR capture failed', err);
        return undefined;
      });
      const url3 = await captureCrossSection().catch((err) => {
        console.warn('[report] AVC CrossSection capture failed', err);
        return undefined;
      });
      setImageSrc({
        crossSection: url3,
        angle0: url2?.[0],
        angle120: url2?.[1],
        angle240: url2?.[2],
        threeD: url,
      });
    } finally {
      closeLoadingModal();
    }
  }

  useEffect(() => {
    capture();
  }, []);

  return (
    <BaseModalContent className='avc' onClose={onClose}
    // vw={35} vh={50}
    title={names.title}>
      <AVCReportModalContent
        imageSrc={imageSrc}
        avcScore={config.avcCalciumScore}
        volume={config.avcCalciumVolume}
      />
    </BaseModalContent>
  );
};

export function useAVCReportModal(res:{updateActivatedTool: (tool: any,check: boolean) => void}) {
  return useReportModal({Component:AVCReportModal,...res});
}
